import { ChevronLeft } from 'lucide-react'
import type { ReactNode, Ref } from 'react'

import { SheetSectionCard } from '@/components/SheetSectionCard'
import { Button } from '@/components/ui/button'
import { TabsContent } from '@/components/ui/tabs'

import { ManagedByBadge, OutOfDateBadge } from '../OrgTooltips'
import { relativeUpdatedAt, type OpenDiff } from '../utils'
import { CompareView, type DiffMode } from './CompareView'
import type { TabKey } from './model'

/** One row of a list, opened in place of the list: what it changed, and when. */
export function DrillIn({
  listLabel,
  diff,
  mode,
  onModeChange,
  backRef,
  onBack,
}: {
  listLabel: string
  diff: OpenDiff
  mode: DiffMode
  onModeChange?: (mode: DiffMode) => void
  backRef: Ref<HTMLButtonElement>
  onBack: () => void
}) {
  const { variant, orgName } = diff
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <div className="flex min-w-0 items-center gap-1 text-sm text-muted-foreground">
        <Button
          ref={backRef}
          type="button"
          variant="ghost"
          size="sm"
          aria-label={`Back to ${listLabel}`}
          className="-ml-2 gap-1 px-2"
          onClick={onBack}>
          <ChevronLeft aria-hidden="true" />
          {listLabel}
        </Button>
        <span aria-hidden="true">/</span>
        <span className="truncate font-medium text-primary-high-contrast">
          {variant.duty_name}
        </span>
      </div>
      <SheetSectionCard
        className="min-w-0"
        body={
          <>
            <CompareView
              from={diff.from}
              to={variant.duty_name}
              oldValue={diff.sharedContent}
              newValue={variant.content}
              mode={mode}
              onModeChange={onModeChange}
            />
            <div className="-mx-3 mt-3 -mb-3 flex flex-wrap items-center gap-2 border-t border-neutral-subtle-border px-3 py-2 text-xs text-muted-foreground">
              <span>{`Updated ${relativeUpdatedAt(variant.updated_at)}`}</span>
              {orgName && (
                <span className="ml-auto flex items-center gap-1">
                  {diff.outOfDate && <OutOfDateBadge orgName={orgName} />}
                  <ManagedByBadge orgName={orgName} />
                </span>
              )}
            </div>
          </>
        }
      />
    </div>
  )
}

/**
 * A list tab that drills into one row. The list stays mounted under the
 * drill-in, so Back can return focus to the row it opened.
 */
export function DrillTab({
  value,
  drillIn,
  children,
}: {
  value: TabKey
  drillIn: ReactNode
  children: ReactNode
}) {
  return (
    // `flex-col`: an active TabsContent is a flex ROW, which sizes a single
    // child to its content across.
    <TabsContent value={value} className="flex-col">
      <div hidden={Boolean(drillIn)}>{children}</div>
      {drillIn}
    </TabsContent>
  )
}
